import { useEffect } from 'react';
import { floorById } from '@/content/floors';
import { FoeElevator } from '@/features/foe-elevator/FoeElevator';
import { VoiceprintScanner } from '@/features/voiceprint-scanner/VoiceprintScanner';
import { QaBadge } from '@/qa/QaBadge';
import { qa } from '@/qa/recorder';
import { useRoute } from './router';
import { HintPreview } from '@/features/model-preview/HintPreview';
import { ModelPreview } from '@/features/model-preview/ModelPreview';
import { TongueDragPreview } from '@/features/model-preview/TongueDragPreview';
import { TongueLabPreview } from '@/features/model-preview/TongueLabPreview';
import { AlignPreview } from '@/features/model-preview/AlignPreview';
import { AudioTest } from '@/features/audio-test/AudioTest';
import { Intro } from '@/features/intro/Intro';
import { AgentBuilder } from '@/features/agent-builder/AgentBuilder';
import { AlexLab } from '@/features/alex-lab/AlexLab';
import { DebugPage } from '@/features/debug/DebugPage';
import { introSeen } from '@/game/intro';
import { DIAGRAMS } from '@/content/diagrams';
import type { DiagramId } from '@/game/coach/cues';
import { POSES, type PoseId } from '@/content/articulation';

/** Development: `?model=<pose>` shows the 3D articulation model on its own. */
const PARAMS = new URLSearchParams(window.location.search);
const MODEL = PARAMS.get('model');
/** Development: `?hint=<diagram>&instead=<diagram>` shows one hint card on its own. */
const HINT = PARAMS.get('hint');
const INSTEAD = PARAMS.get('instead');
/** `?audiotest`: the iPhone sound check (issue #39). */
const AUDIO_TEST = PARAMS.has('audiotest');
/** Development: `?tonguedrag` shows the drag-the-tongue interaction on its own. */
const TONGUE_DRAG = PARAMS.has('tonguedrag');
/** Development: `?tonguelab` shows the Tongue Lab (a different activity each visit). */
const TONGUE_LAB = PARAMS.has('tonguelab');
/** Development: `?align` fits the 3D mouth to a side X-ray picked locally. */
const ALIGN = PARAMS.has('align');
const isDiagram = (id: string | null): id is DiagramId => id !== null && id in DIAGRAMS;

export function App() {
  const route = useRoute();
  const floor = route.page === 'floor' ? floorById(route.floorId) : undefined;
  useEffect(() => {
    qa.event('route', { route: window.location.hash || '#/' });
  }, [route]);
  useEffect(() => {
    const onVisibility = () => {
      qa.event('visibility', { hidden: document.hidden });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
  if (AUDIO_TEST) return <AudioTest />;
  if (route.page === 'debug') return <DebugPage tab={route.tab} />;
  if (TONGUE_DRAG) return <TongueDragPreview />;
  if (TONGUE_LAB) return <TongueLabPreview />;
  if (ALIGN) return <AlignPreview />;
  if (MODEL && MODEL in POSES) return <ModelPreview initial={MODEL as PoseId} />;
  if (isDiagram(HINT))
    return <HintPreview shows={HINT} {...(isDiagram(INSTEAD) ? { insteadOf: INSTEAD } : {})} />;
  // Remount per floor so each mission starts fresh.
  return (
    <>
      {route.page === 'agent' ? (
        <AgentBuilder />
      ) : route.page === 'lab' ? (
        <AlexLab />
      ) : route.page === 'intro' || (route.page === 'elevator' && !introSeen()) ? (
        <Intro />
      ) : floor ? (
        <VoiceprintScanner
          key={`${floor.id}/${route.page === 'floor' ? (route.run ?? '') : ''}`}
          floor={floor}
        />
      ) : (
        <FoeElevator />
      )}
      <QaBadge />
    </>
  );
}
